<template>
  <div>
    <!--面包屑导航区域-->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/index' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>订单管理</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: '/card-grant-activity' }">发卡活动池</el-breadcrumb-item>
      <el-breadcrumb-item>活动发卡记录</el-breadcrumb-item>
    </el-breadcrumb>
    <!--卡片视图区-->
    <el-card>
      <!--活动概览：原先用描述列表三列铺开，标签灰、值小、最后一行还要 span 3，
           整块没有层级，进来第一眼找不到"这是哪个活动"。
           改成概览面板——第一行只放主信息（标题 + 启用状态），下面才是元数据，
           主次靠字号（16 → 12/13）和留白区分，不靠颜色堆砌。
           不加边框（外层 el-card 已有一圈，再套一层是双层框）、不加阴影，
           用一层极浅底色把区块和数据区分开 -->
      <div v-loading="infoLoading" class="activity-overview">
        <div class="activity-overview__head">
          <span class="activity-overview__bar"></span>
          <h3 class="activity-overview__title" :title="activity.title || ''">{{ activity.title || '-' }}</h3>
          <el-tag
            :type="activityTagType"
            size="small"
            class="activity-overview__tag"
          >{{ activityTagText }}</el-tag>
          <el-button
            class="activity-overview__refresh"
            type="text"
            size="small"
            icon="el-icon-refresh"
            :loading="infoLoading"
            @click="refreshActivity"
          >刷新</el-button>
        </div>
        <div class="activity-overview__grid">
          <div class="activity-overview__item">
            <span class="activity-overview__label">活动门店</span>
            <span class="activity-overview__value">{{ activity.storeName || '-' }}</span>
          </div>
          <div class="activity-overview__item">
            <span class="activity-overview__label">活动有效期</span>
            <span class="activity-overview__value">
              {{ formatShortTime(activity.startTime) }}
              <span class="desc-sep">至</span>
              {{ formatShortTime(activity.endTime) }}
            </span>
          </div>
          <div class="activity-overview__item">
            <span class="activity-overview__label">创建人</span>
            <span class="activity-overview__value">{{ activity.createBy || '-' }}</span>
          </div>
          <div class="activity-overview__item">
            <span class="activity-overview__label">创建时间</span>
            <span class="activity-overview__value">{{ formatShortTime(activity.createTime) }}</span>
          </div>
        </div>
      </div>

      <!--筛选域：排版与活动池同一套 order-filter-bar，控件按「常用在前」排，
           沿项目惯例即时生效（@change / 回车 / 清空），不额外摆「查询」按钮。
           记录页保留直接「发卡」入口；活动池点「发卡」仍可通过 sessionStorage 标记自动弹框-->
      <div class="order-filter-bar">
        <el-input
          class="order-filter-bar__item order-filter-bar__item--md"
          v-model="queryInfo.phone"
          placeholder="手机号 / 尾号"
          clearable
          @clear="handleSearch"
          @keyup.enter.native="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>
        <el-input
          class="order-filter-bar__item order-filter-bar__item--md"
          v-model="queryInfo.reason"
          placeholder="发放原因"
          clearable
          @clear="handleSearch"
          @keyup.enter.native="handleSearch"
        ></el-input>
        <!-- 时长只接受数字：输入时就滤掉非数字字符，避免把 'abc' 传给后端（后端按等值比 days） -->
        <el-input
          class="order-filter-bar__item--days"
          v-model="queryInfo.days"
          placeholder="时长(天)"
          clearable
          maxlength="4"
          @input="onDaysInput"
          @clear="handleSearch"
          @change="handleSearch"
          @keyup.enter.native="handleSearch"
        ></el-input>
        <!-- default-time 让「发放结束时间」默认落到当天 23:59:59：
             否则只选日期时结束是 00:00:00，最后一天等于没查（后端是 issue_time <= endTime） -->
        <!-- 前置文字说明这组筛选项查的是发放时间（占位符只在空态可见，选中后就没了，语义靠这行字常驻）；
             竖条与概览面板标题的主色竖条同一套设计语言 -->
        <span class="order-filter-bar__label"><i class="order-filter-bar__label-bar"></i>发放时间查询</span>
        <el-date-picker
          class="order-filter-bar__item--date"
          v-model="queryInfo.dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="yyyy-MM-dd HH:mm:ss"
          :default-time="['00:00:00', '23:59:59']"
          @change="handleSearch"
        ></el-date-picker>
        <el-button class="order-filter-bar__reset" type="text" @click="resetQuery">重置</el-button>
      </div>

      <!--发卡操作区：记录页也保留直接发卡入口；不可发时把原因放在按钮右侧，
           避免只有禁用按钮却不知道为什么不能点-->
      <div class="record-action-bar">
        <el-button
          type="primary"
          size="small"
          icon="el-icon-s-promotion"
          :disabled="!canIssue"
          @click="openGrantDialog"
        >发 卡</el-button>
        <span v-if="!canIssue && issueDisabledReason" class="record-action-bar__reason">
          {{ issueDisabledReason }}
        </span>
      </div>

      <!--发卡记录区域-->
      <!-- border 是列宽拖拽的前提（Element 的 mousedown/mousemove 里都判断 this.border），
           边框色已在样式里压浅 -->
      <el-table
        :data="list"
        class="card-grant-table"
        style="width: 100%"
        border
        stripe
        size="small"
        max-height="760"
        v-loading="loading"
      >
        <template slot="empty">
          <div class="empty-box">
            <i class="el-icon-document"></i>
            <p>{{ appliedFilter ? '没有符合条件的发卡记录' : '这个活动还没有发过卡' }}</p>
          </div>
        </template>
        <el-table-column label="序号" width="60" align="center" class-name="index-col">
          <template slot-scope="scope">
            {{ (queryInfo.pageNum - 1) * queryInfo.pageSize + scope.$index + 1 }}
          </template>
        </el-table-column>
        <!-- 长文本列（发放原因/操作人）才用 min-width 分摊容器剩余宽度，
             其余定长列给固定宽，避免某一列被单独撑到 300+ px -->
        <el-table-column label="手机号" prop="phone" width="140" align="center" class-name="phone-col"></el-table-column>
        <el-table-column label="发放原因" min-width="220" :show-overflow-tooltip="true">
          <template slot-scope="scope">{{ scope.row.reason || '-' }}</template>
        </el-table-column>
        <el-table-column label="时长(天)" prop="days" width="90" align="center" class-name="num-col"></el-table-column>
        <el-table-column label="录入方式" width="100" align="center">
          <template slot-scope="scope">
            {{ formatSource(scope.row.source) }}
          </template>
        </el-table-column>
        <el-table-column label="操作人" min-width="120" :show-overflow-tooltip="true">
          <template slot-scope="scope">{{ scope.row.issueBy || '-' }}</template>
        </el-table-column>
        <el-table-column label="发放时间" prop="issueTime" width="170" :show-overflow-tooltip="true"></el-table-column>
      </el-table>
      <div class="pager-wrap" align="center">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="queryInfo.pageNum"
          :page-sizes="[20, 50, 100]"
          :page-size="queryInfo.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        >
        </el-pagination>
      </div>
    </el-card>

    <!--活动发卡弹窗：门店与活动均只读，由活动带出-->
    <grant-dialog
      v-if="activity.id"
      :visible.sync="grantVisible"
      :store-id="activity.storeId"
      :store-name="activity.storeName"
      :activity-id="activity.id"
      :activity-title="activity.title"
      @activity-updated="handleActivityUpdated"
      @issued="getList"
    ></grant-dialog>
  </div>
</template>

<script>
import GrantDialog from './GrantDialog.vue'
import { getCardGrantPeriodState } from '../../utils/cardGrantTime'

export default {
  name: 'ActivityRecords',

  components: { GrantDialog },

  data() {
    return {
      activityId: null,
      activity: {},
      queryInfo: {
        // 后端 phone 是模糊匹配（LIKE '%?%'），输尾号或前几位都能查到
        phone: '',
        // 发放原因同样是模糊匹配；发卡时是手填的自由文本
        reason: '',
        // 时长按等值筛，用字符串存便于输入框直接双向绑定，提交前再判空
        days: '',
        // 发放时间范围：两个端点都选了才带上 startTime/endTime
        dateRange: [],
        pageNum: 1,
        pageSize: 20
      },
      list: [],
      total: 0,
      // 本次请求是否带了筛选条件：空态文案要按"实际提交的条件"判断，
      // 不能直接用输入框里的值，否则刚敲一半还没查询就被判成"没查到"
      appliedFilter: false,
      loading: false,
      listRequestSeq: 0,
      infoLoading: false,
      activityRequestSeq: 0,
      grantVisible: false,
      pendingAutoIssue: false
    }
  },

  computed: {
    activityTagText() {
      if (!this.activity.id) return this.infoLoading ? '加载中' : '未加载'
      return this.activity.status === 1 ? '启用' : '停用'
    },
    activityTagType() {
      return this.activity.id && this.activity.status === 1 ? 'success' : 'info'
    },
    // 有效期状态：notStart 未开始 / running 进行中 / ended 已结束
    // 只用于判断能不能发卡和给出禁用原因，不在界面上单独展示
    // （"是否有效"由上面「活动有效期」的时间直接看）
    periodState() {
      return getCardGrantPeriodState(this.activity.startTime, this.activity.endTime)
    },
    // 能否发卡：与后端同口径（启用 + 在有效期内 + 门店未停业）。
    // 记录页按钮和活动池带过来的「自动弹出发卡框」共用这个判断
    canIssue() {
      return (
        !!this.activity.id &&
        this.activity.status === 1 &&
        this.activity.storeOpen !== false &&
        this.periodState === 'running'
      )
    },
    // 不能发时给出明确原因：自动弹框被拦下时用它提示，
    // 否则用户从活动池点了「发卡」跳过来却什么都没发生，等于静默失败
    issueDisabledReason() {
      if (!this.activity.id) return this.infoLoading ? '活动信息加载中' : '活动信息未加载'
      if (this.activity.status !== 1) return '该活动已停用，无法发卡'
      if (this.periodState === 'notStart') return '该活动尚未开始，无法发卡'
      if (this.periodState === 'ended') return '该活动已结束，无法发卡'
      if (this.periodState !== 'running') return '该活动有效期不完整，无法发卡'
      if (this.activity.storeOpen === false) return '该门店已停业或不存在，无法发卡'
      return ''
    }
  },

  created() {
    this.activityId = Number(this.$route.params.id)
    // 标志先保留，活动加载成功并真正消费后才删除。
    // 否则详情接口网络失败时标志已经被清掉，用户既等不到自动弹窗，也没有重试入口。
    const autoIssueActivityId = Number(sessionStorage.getItem('cardGrantAutoIssue'))
    this.pendingAutoIssue = autoIssueActivityId === this.activityId
    // 标志指向别的活动时是上次遗留，清掉，避免以后误弹
    if (autoIssueActivityId && autoIssueActivityId !== this.activityId) {
      sessionStorage.removeItem('cardGrantAutoIssue')
    }
    if (!this.activityId) {
      // 非法 id 直接回活动池，避免请求 /cardgrant/activity/NaN
      this.clearPendingAutoIssue()
      this.$router.replace('/card-grant-activity')
      return
    }
    this.loadActivity()
    this.getList()
  },

  beforeDestroy() {
    // 页面离开后丢弃仍在途的响应，避免回包再改已销毁组件的数据
    this.listRequestSeq++
    this.activityRequestSeq++
  },

  methods: {
    async loadActivity() {
      const requestId = ++this.activityRequestSeq
      this.infoLoading = true
      try {
        const { data: res } = await this.$http.get(`cardgrant/activity/${this.activityId}`)
        // 手动刷新连续点击时只认最后一次请求
        if (requestId !== this.activityRequestSeq) return false
        if (res.status === 0 && res.data) {
          this.activity = res.data
          this.maybeAutoIssue()
          return true
        }
        const message = String(res.message || '')
        // 只有明确“活动不存在”才清自动发卡标志并退回活动池；
        // 临时 DB/服务错误留在当前页，顶部「刷新」可重试，自动发卡意图也保留
        if (message.indexOf('活动不存在') !== -1) {
          this.$message.error(message || '活动不存在')
          this.clearPendingAutoIssue()
          this.$router.replace('/card-grant-activity')
          return false
        }
        this.$message.error((message || '查询活动失败') + '，请点击「刷新」重试')
        return false
      } catch (error) {
        if (requestId !== this.activityRequestSeq) return false
        console.error('查询活动失败：', error)
        this.$message.error('查询失败：' + (error.message || '未知错误'))
        return false
      } finally {
        if (requestId === this.activityRequestSeq) this.infoLoading = false
      }
    },
    // 活动可能在别的标签页/别的管理员手里被改过：手动重拉概览和台账，
    // 避免顶部还显示旧标题/旧有效期，发卡时才发现活动已过期
    async refreshActivity() {
      const loaded = await this.loadActivity()
      if (loaded) this.getList()
    },
    // 发卡弹窗提交前拉到了最新活动：同步回概览和 canIssue，避免页面继续用旧值
    handleActivityUpdated(activity) {
      if (activity && activity.id === this.activityId) this.activity = activity
    },
    // 消费自动发卡标志：成功弹出或明确不可发后再清掉，网络失败时保留以便刷新重试
    clearPendingAutoIssue() {
      this.pendingAutoIssue = false
      sessionStorage.removeItem('cardGrantAutoIssue')
    },
    // 记录页直接发卡入口
    openGrantDialog() {
      if (!this.canIssue) {
        this.$message.warning(this.issueDisabledReason || '当前活动不可发卡')
        return
      }
      this.grantVisible = true
    },
    // 从活动池点「发卡」进来时自动弹出发卡框：活动信息加载完才能判可发。
    // 标志只在这次动作真正处理后消费，避免详情接口网络失败后标志丢失
    maybeAutoIssue() {
      if (!this.pendingAutoIssue) return
      this.clearPendingAutoIssue()
      if (!this.canIssue) {
        this.$message.warning(this.issueDisabledReason || '当前活动不可发卡')
        return
      }
      this.grantVisible = true
    },
    // 台账分页查询
    async getList() {
      const requestId = ++this.listRequestSeq
      const phone = (this.queryInfo.phone || '').trim()
      const reason = (this.queryInfo.reason || '').trim()
      const days = (this.queryInfo.days || '').trim()
      const range = this.queryInfo.dateRange || []
      this.appliedFilter = !!(phone || reason || days) || range.length === 2
      this.loading = true
      try {
        const params = {
          activityId: this.activityId,
          phone: phone || undefined,
          reason: reason || undefined,
          days: days || undefined,
          page: this.queryInfo.pageNum,
          limit: this.queryInfo.pageSize
        }
        // 发放时间区间：两端都有才按区间过滤（后端是 issue_time >= / <=）
        if (range.length === 2) {
          params.startTime = range[0]
          params.endTime = range[1]
        }
        const { data: res } = await this.$http.get('cardgrant/records', { params })
        // 快速切换筛选时只认最后一次请求，旧响应不能覆盖新结果
        if (requestId !== this.listRequestSeq) return
        if (res.status === 0) {
          this.list = res.list || []
          this.total = res.total || 0
        } else {
          this.$message.error(res.message || '查询失败')
        }
      } catch (error) {
        if (requestId !== this.listRequestSeq) return
        console.error('查询发卡记录失败：', error)
        this.$message.error('查询失败：' + (error.message || '未知错误'))
      } finally {
        if (requestId === this.listRequestSeq) this.loading = false
      }
    },
    // 筛选条件查询（手机号/发放原因回车、清空、时间范围选完、时长失焦/回车都走这里）：
    // 条件一变旧的页码就没意义，回第一页
    handleSearch() {
      this.queryInfo.pageNum = 1
      this.getList()
    },
    // 时长只收数字：就地滤掉非数字字符，后端 days 是数值等值比较，
    // 传 'abc' 过去会变成"这个条件不生效"，比报错更难排查
    onDaysInput(val) {
      this.queryInfo.days = String(val == null ? '' : val).replace(/\D/g, '')
    },
    // 重置：清掉全部筛选条件后回第一页重查
    resetQuery() {
      this.queryInfo.phone = ''
      this.queryInfo.reason = ''
      this.queryInfo.days = ''
      this.queryInfo.dateRange = []
      this.queryInfo.pageNum = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.queryInfo.pageSize = val
      this.queryInfo.pageNum = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.queryInfo.pageNum = val
      this.getList()
    },
    // 录入方式：1=逐条添加，2=Excel导入；null/未知值不能再冒充逐条添加
    formatSource(source) {
      const value = Number(source)
      if (value === 1) return '逐条添加'
      if (value === 2) return 'Excel导入'
      return '未知来源'
    },
    // 有效期只展示到分钟，"秒"在这个场景没有意义（都是 00 或 59），去掉能省 5 个字符
    formatShortTime(time) {
      if (!time) return '-'
      const str = String(time)
      return str.length >= 16 ? str.slice(0, 16) : str
    }
  }
}
</script>

<style scoped>
/* 面包屑默认被全局 style.css 设成 width:90% + 居中，会比卡片左缘内缩，
   这里按订单列表页的做法压回整宽，保证与卡片严格左对齐 */
.el-breadcrumb {
  width: 100%;
}
/* ===== 活动概览 ===== */
/* 不加 1px 边框：外层 el-card 自己已有一圈框，里面再套一层就是双层框，最显廉价。
   改用极浅底色区分区块，靠留白和一条内部分割线分区。
   圆角取 4px 与 el-card 一致：内层比外层更圆会显得套歪 */
.activity-overview {
  padding: 16px 20px;
  margin-bottom: 16px;
  background-color: #fafbfc;
  border-radius: 4px;
}
.activity-overview__head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
/* 3px 主色竖条给标题一个视觉锚点，整块不至于「飘」在卡片里 */
.activity-overview__bar {
  flex: 0 0 3px;
  height: 16px;
  border-radius: 2px;
  background-color: #409eff;
}
/* min-width:0 是省略号生效的前提：flex 子项默认 min-width:auto 不会收缩 */
.activity-overview__title {
  min-width: 0;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.activity-overview__tag {
  flex: 0 0 auto;
}
/* 概览区右侧的操作入口：只做信息刷新，不抢主信息的注意力 */
.activity-overview__refresh {
  flex: 0 0 auto;
  margin-left: auto;
}
/* 元数据横排：标签固定 64px（正好放得下「活动有效期」5 个汉字），值跟在后面。
   用内容宽度而不是等分栅格——卡片有一千多像素宽，四等分会把每格撑得很空。
   分割线上下各留 12px，是面板内部唯一的一条线，靠它把主信息区和元数据分开 */
.activity-overview__grid {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px 40px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}
.activity-overview__item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.activity-overview__label {
  flex: 0 0 64px;
  font-size: 12px;
  line-height: 20px;
  color: #909399;
}
/* 值与标签差两级（14 vs 12），比原来 13/12 那种「差半级」看得清主次 */
.activity-overview__value {
  font-size: 14px;
  line-height: 20px;
  color: #303133;
  word-break: break-all;
}
/* 「至」只是分隔符，压成浅灰退到背景里 */
.desc-sep {
  margin: 0 8px;
  color: #c0c4cc;
}
/* ===== 筛选域（同活动池 / 订单列表 order-filter-bar 模式）===== */
/* 上下间距都交给概览面板的 margin-bottom 和这条的 padding-bottom，不再叠 margin-top */
.order-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}
.order-filter-bar__item {
  /* 基础档位；本页手机号、发放原因同时挂了 --md，实际按 --md 的宽度走 */
  flex: 0 0 140px;
}
.order-filter-bar__item--md {
  /* 手机号：11 位数字 + 右侧查询按钮；发放原因：自由文本，两者都给这一档 */
  flex: 0 0 200px;
}
.order-filter-bar__item--days {
  /* 时长是天数，只输 1-4 位数字，窄一档避免占位 */
  flex: 0 0 120px;
}
.order-filter-bar__item--date {
  /* 日期范围独立档位，不参与伸缩（挂 __item 会被固定宽度覆盖）。
     400px 不是随手取的：Element 给 datetimerange 的默认宽度就是 400，
     内部两个输入各占 39%，19 个字符（2026-10-23 14:00:00）在 14px 下约 130px，
     压到 340px 时每个输入只剩 125px，「秒」会被截掉看不见 */
  flex: 0 0 400px;
}
/* 日期框前置文字：比占位文字深一档（#303133）+ 500 字重当「字段名」，
   前面 3px 主色竖条与概览面板标题的锚点竖条（activity-overview__bar）同一套语言，
   尺寸按 14px 文字等比缩到 12px 高；nowrap 防止窄屏换行时 6 个字被拆成两行 */
.order-filter-bar__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  white-space: nowrap;
}
.order-filter-bar__label-bar {
  width: 3px;
  height: 12px;
  border-radius: 2px;
  background-color: #409eff;
}
/* 重置是文字按钮（没有边框），与左侧日期框多留 4px，避免看起来贴着 */
.order-filter-bar__reset {
  flex: 0 0 auto;
  margin-left: 4px;
}
/* Element UI 2.x 的 el-input 根节点自带 width:100%，
   在 flex 容器里必须显式设置，否则宽度不受 flex 控制 */
.order-filter-bar >>> .el-input {
  width: 100%;
}
/* 日期范围内部的输入框字号默认 13px，比 el-input 的 14px 小一号，
   放一排会明显不齐，这里统一到 14px */
.order-filter-bar >>> .el-range-input {
  font-size: 14px;
}
/* 记录页发卡操作区：按钮和禁用原因同一行，原因紧跟在按钮右侧 */
.record-action-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.record-action-bar__reason {
  font-size: 12px;
  line-height: 20px;
  color: #f56c6c;
}
/* ===== 表格 ===== */
/* 列宽拖拽开了 border，默认边框色在浅色表格上偏重，统一压浅一档 */
.card-grant-table >>> .el-table__header-wrapper th,
.card-grant-table >>> .el-table__body-wrapper td {
  border-color: #ebeef5;
}
/* 拖拽没有可见手柄，只有 hover 到右缘 8px 才变光标，这里把 hover 列的右分隔线提亮做提示 */
.card-grant-table >>> .el-table__header-wrapper th:hover {
  border-right-color: #a0cfff;
}
/* 拖动时跟随鼠标的参考线，默认与边框同色几乎看不见，提成主色 */
.card-grant-table >>> .el-table__column-resize-proxy {
  border-left-color: #409eff;
}
/* Element 默认表头是白底 + 12px 浅灰字，和白色表体几乎分不开。
   改成浅灰底 + 深色半粗字 + 加高，把“字段名”和“数据”在视觉上分成两层。
   这条必须写在上面 border-color 之后：border-bottom 的简写会把底边覆盖成 #e4e7ed */
.card-grant-table >>> .el-table__header-wrapper th {
  height: 44px;
  background-color: #f5f7fa;
  color: #303133;
  font-weight: 600;
  border-bottom: 1px solid #e4e7ed;
}
/* 表体行也加高，表头变重之后行距不至于显得发挤 */
.card-grant-table >>> .el-table__body-wrapper td {
  padding-top: 9px;
  padding-bottom: 9px;
}
.phone-col {
  font-family: Consolas, Monaco, 'Courier New', monospace;
}
/* 序号是辅助信息，压成浅灰退到背景里，别和数据抢注意力 */
.card-grant-table >>> .index-col {
  color: #909399;
}
/* 时长是数值列：等宽数字，上下行的数字位对齐才扫得动 */
.card-grant-table >>> .num-col {
  font-variant-numeric: tabular-nums;
  font-family: Consolas, Monaco, 'Courier New', monospace;
}
/* ===== 分页 / 空态 ===== */
/* 分页与表格之间用固定间距，替掉原来靠 <br> 撑开的做法 */
.pager-wrap {
  padding-top: 16px;
}
/* 空态：给一句可执行的提示，而不是干巴巴的“暂无数据” */
.empty-box {
  padding: 24px 0 16px;
  color: #909399;
}
.empty-box i {
  font-size: 32px;
  color: #dcdfe6;
  display: block;
  margin-bottom: 8px;
}
.empty-box p {
  margin: 0;
  font-size: 14px;
}
</style>
